/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isIOS } from 'react-device-detect';
import Slider from '@mui/material/Slider';
import { getAddressSugestions, getLocation, fetchAddressByCoordinates } from 'utils/Coordinates/coordinatesFunctions';
import SimpleMap from 'components/MapView/SimpleMap';
import countryService from 'services/countryService';
import api from 'services/api';
import useResponsive from '../../hooks/useResponsive';
import InputSearch from '../input-search';

import './address-selector.scss';

const normalizeString = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const defaultCoordsByCountryId = {
  101: {
    latitude: -34.8748804,
    longitude: -56.1704501,
  },
  102: {
    latitude: -31.4200832,
    longitude: -64.1887761,
  },
};

const AddressSelector = ({
  title, subtitle, onChange, current,
}) => {
  const { isNotDesktop } = useResponsive();
  const countryId = countryService.getCurrentCountryId();
  const defaultCoords = defaultCoordsByCountryId[countryId];

  const startCoords = !current?.latitude ? defaultCoords : {
    latitude: current.latitude,
    longitude: current.longitude,
  };
  // Selections
  // const [rangeValue, setRangeValue] = useState(current?.range ?? 0);
  const setRangeValue = (newVal) => onChange({ range: newVal });
  const [locationCoords, setLocationCoords] = useState(startCoords);
  // const [mapLoading, setMapLoading] = useState(false);
  const [locationSelectedByUser, setLocationSelectedByUser] = useState(false);

  // address
  const [addressInput, setAddressInput] = useState({ search: false, value: current?.addressTxt ?? '' });
  const [addressSugestions, setAddressSugestions] = useState([]);
  const resetAddressSuggestions = () => setAddressSugestions([]);
  const [inputDropdownLoading, setInputDropdownLoading] = useState(false);

  // general
  const [locationIsFromDiffCountry, setLocationIsFromDiffCountry] = useState(false);
  const [myLocation, setMyLocation] = useState(undefined);
  const [locationToRecenter, setLocationToRecenter] = useState();

  useEffect(() => {
    if (locationCoords.latitude !== defaultCoords.latitude
      || locationCoords.longitude !== defaultCoords.longitude
    ) {
      setLocationSelectedByUser(true);
      onChange({ ...locationCoords, addressTxt: addressInput.value });
    } else {
      setAddressInput({ search: false, value: '' });
    }
  }, [locationCoords]);

  /* useEffect(() => {
    onChange({ range: rangeValue });
  }, [rangeValue]); */

  const updateDataByCoords = async (coords) => {
    // setMapLoading(true);
    setLocationCoords(coords);
    setLocationToRecenter(coords);
    setAddressInput({ search: false, value: coords.address });
    // setMapLoading(false);
  };

  // search for address x secs after the user stopped typing
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (addressInput.search && addressInput.value && addressInput.value.length >= 3) {
      const timeoutId = setTimeout(() => {
        setInputDropdownLoading(true);
        getAddressSugestions(addressInput.value, setAddressSugestions)
          .finally(() => setInputDropdownLoading(false));
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
    resetAddressSuggestions();
    if (!addressInput.search && addressInput.value) {
      onChange({ addressTxt: addressInput.value });
    }
  }, [addressInput]);

  // updates map when address is selected
  const setSelectedOption = async (item) => {
    api.get(`google/coords?placeId=${item.place_id}`)
      .then((c) => {
        const { lat, lng } = c.data.result.geometry.location;
        const coords = { latitude: lat, longitude: lng, address: item.description };
        updateDataByCoords(coords);
        /* console.log('setSelectedOption', locationCoords);
        onChange(locationCoords); */
      });
  };

  const isFromCountry = (payload) => {
    const expectedCountryName = countryService.getCurrentCountryName();
    const countryName = payload.data.results.filter((r) => r.types.includes('country'));
    return countryName[0].formatted_address === expectedCountryName;
  };

  const updateCoordsAndGetAddress = async (coords) => {
    setInputDropdownLoading(true);
    setLocationToRecenter(coords);
    await fetchAddressByCoordinates(coords)
      .then((a) => {
        if (!isFromCountry(a)) {
          setLocationIsFromDiffCountry(true);
        } else {
          setLocationIsFromDiffCountry(false);
          setLocationCoords(coords);
        }

        setAddressInput({ search: false, value: a.data.results[0].formatted_address });
      });
    setInputDropdownLoading(false);
  };

  useEffect(() => {
    if (myLocation) updateCoordsAndGetAddress(myLocation);
  }, [myLocation]);

  const getMyLocation = async () => {
    setInputDropdownLoading(true);
    getLocation(setMyLocation, setMyLocation);
  };

  const onChangeInput = ({ target: { value } }) => {
    const normalizedString = normalizeString(value);
    setAddressInput({ search: true, value: normalizedString });
  };

  function getThumbPosition() {
    const calc1 = ((Number(current?.range ?? 0) * 100) / 50);

    // first magic number sets the start and second sets the ending
    return `calc(${calc1}% - (${calc1 * 0.43}px))`;
  }

  const outputCustomStyle = {
    left: `${getThumbPosition()}`,
    top: '-10px',
    color: '#1A1A1A',
    width: '50px',
    textAlign: 'center',
  };

  const onChangeRange = ({ target: { value } }) => {
    const newRange = Number(value);
    setRangeValue(newRange);
  };

  const onResetSearch = () => {
    onChangeInput({ target: { value: '' } });
  };

  const onMapClick = (lat, lng) => {
    updateCoordsAndGetAddress({ latitude: lat, longitude: lng });
  };

  return (
    <div className="address-selector">
      {!!title && (
      <div className="address-selector__title">
        {title}
      </div>
      )}
      <div className="address-selector__options">
        {!!subtitle && (
        <div className="address-selector__subtitle">
          {subtitle}
        </div>
        )}
        <div className="address-selector__search">
          <InputSearch
            placeholder="Ingresá una dirección"
            value={addressInput.value}
            onChange={onChangeInput}
            onReset={onResetSearch}
            loading={inputDropdownLoading}
          />
          <button
            className="address-selector__button"
            type="button"
            onClick={getMyLocation}
          >
            <svg
              className="my-location"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.37498 17.8749V17.0576C7.63887 16.8792 6.17733 16.1965 4.99035 15.0096C3.8034 13.8226 3.12071 12.361 2.94229 10.6249H2.12498C1.9479 10.6249 1.79947 10.565 1.67969 10.4452C1.5599 10.3253 1.5 10.1768 1.5 9.99969C1.5 9.82252 1.5599 9.67411 1.67969 9.55446C1.79948 9.43481 1.94791 9.37498 2.12498 9.37498H2.94229C3.12071 7.63887 3.8034 6.17733 4.99035 4.99035C6.17733 3.8034 7.63887 3.12071 9.37498 2.94229V2.12498C9.37498 1.94791 9.4349 1.79948 9.55473 1.67969C9.67458 1.5599 9.82308 1.5 10.0002 1.5C10.1774 1.5 10.3258 1.5599 10.4455 1.67969C10.5651 1.79948 10.6249 1.94791 10.6249 2.12498V2.94229C12.3611 3.12071 13.8226 3.8034 15.0096 4.99035C16.1965 6.17733 16.8792 7.63887 17.0576 9.37498H17.8749C18.052 9.37498 18.2004 9.4349 18.3202 9.55473C18.44 9.67458 18.4999 9.82308 18.4999 10.0002C18.4999 10.1774 18.44 10.3258 18.3202 10.4455C18.2004 10.5651 18.052 10.6249 17.8749 10.6249H17.0576C16.8792 12.361 16.1965 13.8226 15.0096 15.0096C13.8226 16.1965 12.3611 16.8792 10.6249 17.0576V17.8749C10.6249 18.052 10.565 18.2005 10.4452 18.3202C10.3253 18.44 10.1768 18.4999 9.99969 18.4999C9.82252 18.4999 9.67411 18.44 9.55446 18.3202C9.43481 18.2004 9.37498 18.052 9.37498 17.8749ZM9.99996 15.8333C11.6111 15.8333 12.9861 15.2638 14.125 14.125C15.2638 12.9861 15.8333 11.6111 15.8333 9.99996C15.8333 8.38885 15.2638 7.01385 14.125 5.87496C12.9861 4.73607 11.6111 4.16663 9.99996 4.16663C8.38885 4.16663 7.01385 4.73607 5.87496 5.87496C4.73607 7.01385 4.16663 8.38885 4.16663 9.99996C4.16663 11.6111 4.73607 12.9861 5.87496 14.125C7.01385 15.2638 8.38885 15.8333 9.99996 15.8333ZM9.99996 12.9166C9.19789 12.9166 8.51127 12.631 7.94008 12.0598C7.3689 11.4887 7.08331 10.802 7.08331 9.99996C7.08331 9.19789 7.3689 8.51126 7.94008 7.94008C8.51127 7.3689 9.19789 7.08331 9.99996 7.08331C10.802 7.08331 11.4887 7.3689 12.0598 7.94008C12.631 8.51126 12.9166 9.19789 12.9166 9.99996C12.9166 10.802 12.631 11.4887 12.0598 12.0598C11.4887 12.631 10.802 12.9166 9.99996 12.9166ZM9.99996 11.6666C10.4583 11.6666 10.8507 11.5034 11.177 11.177C11.5034 10.8507 11.6666 10.4583 11.6666 9.99996C11.6666 9.54163 11.5034 9.14926 11.177 8.82288C10.8507 8.49649 10.4583 8.33329 9.99996 8.33329C9.54163 8.33329 9.14926 8.49649 8.82288 8.82288C8.49649 9.14926 8.33329 9.54163 8.33329 9.99996C8.33329 10.4583 8.49649 10.8507 8.82288 11.177C9.14926 11.5034 9.54163 11.6666 9.99996 11.6666Z"
                fill="black"
                fillOpacity="0.9"
              />
            </svg>
            {!isNotDesktop && <div className="button-label">Utilizar mi ubicación</div>}
          </button>
        </div>
        {!!addressSugestions.length && (
          <div className="address-selector__list">
            {
              addressSugestions.map((suggestion) => (
                <div
                  key={suggestion.description}
                  className="address-selector__item"
                  onClick={() => setSelectedOption(suggestion)}
                >
                  {suggestion.description}
                </div>
              ))
            }
          </div>
        )}
        {locationCoords && (
          <div className="address-selector__map">
            <SimpleMap
              latitude={locationCoords.latitude}
              longitude={locationCoords.longitude}
              heightSize="100%"
              radius={(current?.range ?? 0) * 1000}
              handleClickLatLong={onMapClick}
              hidePin={!locationSelectedByUser}
              locationToRecenter={locationToRecenter}
            />
          </div>
        )}
        {locationIsFromDiffCountry && (
          <p>
            La dirección no corresponde al país
          </p>
        )}
      </div>
      <div className={`location__range__container ${locationSelectedByUser ? 'shown' : ''}`}>
        <div className="location__range__title">
          <h5>Seleccioná un rango de búsqueda</h5>
        </div>
        <div className="location__range sidebar-range">
          {false && (
            <output style={outputCustomStyle}>
              <strong>{`${current?.range ?? 0} km`}</strong>
            </output>
          )}
          <Slider
            id="distance-slider"
            className={`range__slider flow__slider ${!current?.range ? 'invalid' : ''}`}
            aria-label="Rango de búsqueda"
            // defaultValue={0}
            value={current?.range ?? 0}
            onChange={(event, newValue) => {
              if (isIOS) {
                if (event.type === 'mouseup' || event.type === 'mousedown') {
                  return;
                }
              }
              setRangeValue(newValue);
            }}
            getAriaValueText={(value) => `${value} km`}
            valueLabelFormat={(value) => `${value} km`}
            step={1}
            valueLabelDisplay="on"
            min={0}
            max={50}
            // style={{ width: '95%' }}
            marks={[
              { value: 0, label: '0' },
              /* { value: 1 },
              { value: 2 },
              { value: 3 },
              { value: 4 }, */
              { value: 5 },
              /* { value: 6 },
              { value: 7 },
              { value: 8 },
              { value: 9 }, */
              { value: 10, label: '10' },
              /* { value: 15, label: '10' }, */
              { value: 20, label: '20' },
              /* { value: 25, label: '10' }, */
              { value: 30, label: '30' },
              /* { value: 35, label: '10' }, */
              { value: 40, label: '40' },
              /* { value: 45, label: '10' }, */
              { value: 50, label: '50' },
            ]}
          />
          {false && (
            <input
              type="range"
              className="location__range__input"
              name="range-value"
              value={current?.range ?? 0}
              onChange={onChangeRange}
              min="0"
              max="50"
            />
          )}
        </div>
        <div className="location__range__underbar" style={{ display: 'none' }}>
          <p className="smaller__range">0</p>
          <p className="smaller__range">10</p>
          <p className="smaller__range">20</p>
          <p className="smaller__range">30</p>
          <p className="smaller__range">40</p>
          <p className="smaller__range">50</p>
        </div>
      </div>
    </div>
  );
};

AddressSelector.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  current: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    range: PropTypes.number,
    addressTxt: PropTypes.string,
  }),
};

AddressSelector.defaultProps = {
  title: null,
  subtitle: null,
  current: null,
};

export default AddressSelector;
